/**
 * Enums fechados do funil de aquisição do teste grátis (GA4).
 *
 * Nada fora destes valores pode ser enviado ao GA4 nem gravado em storage.
 * Isso é o que garante a regra de privacidade: só enum, nunca dado livre
 * (nome, e-mail, telefone, id, pathname bruto, query string...).
 */

/** Nomes de evento enviados ao GA4. */
export const EVENTS = Object.freeze({
  TRIAL_CTA_CLICK: "trial_cta_click",
  SIGN_UP: "sign_up",
  GENERATE_LEAD: "generate_lead",
});

/** Posição do CTA dentro da página. */
export const CTA_PLACEMENTS = Object.freeze({
  HEADER_DESKTOP: "header_desktop",
  HEADER_MOBILE: "header_mobile",
  HERO: "hero",
  MIDDLE: "middle",
  FEATURES: "features",
  PLANS: "plans",
  FINAL: "final",
});

/** Página de origem do clique (derivada da rota, nunca do pathname bruto). */
export const CTA_PAGES = Object.freeze({
  HOME: "home",
  AGENDAMENTO: "agendamento",
  ATENDIMENTO: "atendimento",
  BARBEARIAS: "barbearias",
  CONSULTORIOS: "consultorios",
  MECANICAS: "mecanicas",
  LAVA_JATOS: "lava_jatos",
  ACADEMIAS: "academias",
  RECURSOS: "recursos",
  UNKNOWN: "unknown",
  DIRECT: "direct",
});

/** Segmento de negócio associado à página. */
export const BUSINESS_SEGMENTS = Object.freeze({
  GENERAL: "general",
  BARBEARIAS: "barbearias",
  CONSULTORIOS: "consultorios",
  MECANICAS: "mecanicas",
  LAVA_JATOS: "lava_jatos",
  ACADEMIAS: "academias",
});

/** Destino real para onde o CTA navega. */
export const DESTINATIONS = Object.freeze({
  REGISTRATION: "/registration",
  LOGIN_SIGNUP: "/login?tab=signup",
});

/** Origem do lead — hoje só existe o cadastro de teste grátis. */
export const LEAD_SOURCE = Object.freeze({
  FREE_TRIAL_SIGNUP: "free_trial_signup",
});

/** Como o lead chegou: veio de um CTA rastreado ou entrou direto. */
export const ATTRIBUTION = Object.freeze({
  CTA: "cta",
  DIRECT: "direct",
});

/** Tipo de plano associado ao CTA (só o gratuito é rastreado). */
export const PLAN_TYPE = Object.freeze({
  FREE: "free",
});

/**
 * Qual dos dois produtos o CTA está oferecendo.
 *
 * Sem esta dimensão o GA4 mostraria um funil só, e a pergunta que importa —
 * "qual produto converte?" — não teria resposta.
 *
 * ⚠️ **Re-exportado, não redefinido.** O enum é o mesmo que viaja no `?produto=`
 * da URL e no corpo do signup; a fonte é `src/constants/trialProduct.js`
 * (INV-04). Redeclarar os literais aqui criaria dois enums que ninguém obriga a
 * concordar — e a divergência não daria erro: o backend cairia no default e
 * entregaria o trial errado em silêncio.
 */
export { TRIAL_PRODUCTS as PRODUCTS } from "../constants/trialProduct";

/** Método de cadastro. */
export const SIGN_UP_METHOD = Object.freeze({
  EMAIL_PASSWORD: "email_password",
});

/**
 * Valor sentinela usado quando o cadastro acontece sem contexto de CTA.
 * Nunca é gravado em storage — só aparece no `generate_lead` direto.
 */
export const DIRECT_VALUE = "direct";

/* Listas de valores — usadas na validação de enum. */
export const CTA_PLACEMENT_VALUES = Object.freeze(
  Object.values(CTA_PLACEMENTS),
);
export const CTA_PAGE_VALUES = Object.freeze(Object.values(CTA_PAGES));
export const BUSINESS_SEGMENT_VALUES = Object.freeze(
  Object.values(BUSINESS_SEGMENTS),
);
export const DESTINATION_VALUES = Object.freeze(Object.values(DESTINATIONS));
export const PLAN_TYPE_VALUES = Object.freeze(Object.values(PLAN_TYPE));
export { TRIAL_PRODUCT_VALUES as PRODUCT_VALUES } from "../constants/trialProduct";

/** Janela de validade do contexto de atribuição: 30 minutos. */
export const CTA_CONTEXT_TTL_MS = 30 * 60 * 1000;

/** Chave própria no sessionStorage (nunca localStorage). */
export const CTA_CONTEXT_STORAGE_KEY = "nx_trial_cta_ctx";
